import {
  action,
  observable,
  runInAction,
  computed,
  reaction,
} from 'mobx';
import Api from '../../api';
import Transaction from '../../domain/entities/Transaction/Transaction';
import { INotification, IOrderSummaryResponse } from '../../api/IApiResponses';
import Address from '../../domain/entities/Address';
import ShippingRepository from '../ShippingRepository';
import BillingRepository from '../BillingRepository';
import Order from '../../domain/entities/Order/Order';
import { IFromEstimatedDeliveryMapping, IFromShippingMethodMapping, IFromTransactionMapping } from '../../api/IDataMappings';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import { PAYMENT_ERRORS, SEVERITY_ABORT } from '../../constants/Errors';
import { ADDRESS_TYPE_ID, DeliveryOptions, FIELD_TYPE } from '../../constants/Constants';
import { normalizeErrorElements } from '../../helpers/normalizeApiErrors';
import SHIPPING_METHODS from '../../constants/ShippingMethods';
import SetNotificationsFromTransactionUseCase from '../../use-cases/SetNotificationsFromTransactionUseCase';
import EnhanceNotificationsCollectionUseCase from '../../use-cases/EnhanceNotificationsCollectionUseCase';
import ConfigRepository from '../ConfigRepository';
import SalonRepository from '../SalonRepository';
import DateUtils from '../../utils/DateUtils';
import { NOTIFICATION_GROUP, NOTIFICATION_TYPE } from '../../constants/Notifications';
import { ApiError } from '../../api/transport/HttpTransport';
import CheckTransactionBreakingMissingData from '../../use-cases/CheckTransactionBreakingMissingData';
import { IShippingMethod } from '../../domain/entities/ShippingMethodsCollection/ShippingMethodsCollection';

export interface ITransactionLoadOptions {
  forceReload: boolean;
  debounceRequest: boolean;
  reloadShipMethods?: boolean;
  selectedCountryId?: number;
}

const defaultTransactionLoadOptions: ITransactionLoadOptions = {
  debounceRequest: false,
  forceReload: false,
  reloadShipMethods: false,
};

export interface ITransactionRepositoryConfig {
  api: Api;
  billingRepository: BillingRepository;
  shippingRepository: ShippingRepository;
  notificationsCollection: NotificationsCollection;
  configRepository: ConfigRepository;
  salonRepository: SalonRepository;
}

export default class TransactionRepository {
  @observable transaction: Transaction;

  @observable order: IOrderSummaryResponse;

  @observable notificationsCollection: NotificationsCollection;

  private api: Api;

  private shippingRepository: ShippingRepository;

  private billingRepository: BillingRepository;

  private transactionPromise: Promise<Transaction> | null = null;

  private configRepository: ConfigRepository;

  private salonRepository: SalonRepository;

  private hasIrreconcilableError = false;

  constructor(config: ITransactionRepositoryConfig) {
    this.api = config.api;
    this.configRepository = config.configRepository;
    this.billingRepository = config.billingRepository;
    this.shippingRepository = config.shippingRepository;
    this.notificationsCollection = config.notificationsCollection;
    this.salonRepository = config.salonRepository;

    this.transaction = new Transaction();

    // load new transaction when adding or removing a gift card
    reaction(() => this.transaction.payment.giftCards.length, () => this.loadTransaction());

    // load new transaction if user selects a not free ship method in zero checkout flow
    reaction(() => this.transaction.shouldResetZeroPayment, () => this.loadTransaction());

    reaction(() => this.notificationsCollection.hasNotification(PAYMENT_ERRORS.CART_CHANGED),
      hasCartChangedError => {
        if (hasCartChangedError) {
          this.loadTransaction();
        }
      });

    reaction(() => this.shippingRepository.isUpdating, () => {
      if (this.transaction.shippingAddress.isRestrictedAddress
        && !this.shippingRepository.formStore.isRestrictedAddress) {
        this.loadTransaction();
      }
    });
  }

  // move to transaction entity once addresses are there
  @computed
  get shouldSelectShippingAddress(): boolean {
    return !(
      this.transaction.isAvailable
      && this.transaction.shippingAddress?.isNewAddress
      && !this.notificationsCollection.hasRestrictedAddressNotifications);
  }

  // move to transaction entity once addresses are there
  @computed
  get shouldSelectBillingAddress(): boolean {
    return (
      !(this.transaction.isAvailable
        && (this.transaction.billingAddress.isNewAddress && this.transaction.isBillingAddressRequired))
    );
  }

  isTransactionKeyValid = async (): Promise<boolean> => {
    const response = await this.api.setTransactionKey(this.transaction.transKey);
    return response.validatedTrans;
  };

  getTransaction = async (bypassActiveRequest = false): Promise<Transaction> => {
    if (!this.transaction.isAvailable && !this.hasIrreconcilableError) {
      return this.loadTransaction();
    }

    if (this.transactionPromise && !bypassActiveRequest) {
      return this.transactionPromise;
    }

    return this.transaction;
  };

  @action
  public loadTransaction = async (options: ITransactionLoadOptions = defaultTransactionLoadOptions, signal?: AbortSignal): Promise<Transaction> => {
    if (this.transactionPromise && !options.forceReload) {
      return this.transactionPromise;
    }


    // eslint-disable-next-line no-async-promise-executor
    this.transactionPromise = new Promise(async (resolve, reject) => {
      try {
        let selectedCountryId;
        if (this.configRepository.hasCrossBorderShipping) {
          // for cross border country shipping sites, we need to append country id param to transaction API to get the corresponding delivery methods
          selectedCountryId = this.isNewOrEditAddress ?
            this.shippingRepository.formStore.values?.country_id :
            this.transaction?.shippingAddress?.attributes?.country_id;
        }
        // note that a debounced request, will only resolve last, other promises will not even get rejected
        const trans: IFromTransactionMapping = options.debounceRequest ? await this.api.getTransactionDebounced(selectedCountryId, { signal }) : await this.api.getTransaction(selectedCountryId, { signal });

        runInAction(() => {
          this.api.setTransId(trans.transId);

          if (trans.deliveryOptions) {
            this.transaction.setSelectedShippingMethodId(trans.selectedShippingMethodId);
          }

          this.setShippingAddresses(trans);

          this.setBillingAddress(trans);

          if (trans.errors) {
            const normalizedErrors = normalizeErrorElements(
              trans.errors,
              NOTIFICATION_GROUP.SHIPPING,
            );
            this.notificationsCollection.addNotifications(normalizedErrors);
          }
          if (trans.notifications) {
            trans.notifications.forEach((notification: INotification) => {
              const notificationType = notification.severity === SEVERITY_ABORT ?
                NOTIFICATION_TYPE.DANGER : NOTIFICATION_TYPE.WARNING;

              this.notificationsCollection.addNotification({
                ...notification,
                type: notificationType,
              });
            });
            const setNotificationsFromTransaction = new SetNotificationsFromTransactionUseCase();
            setNotificationsFromTransaction.execute();
          }
          this.transaction.updateTransaction(Transaction.fromJSON(trans));
          if (!this.salonRepository.isUserSalonSet && trans.userSalon) {
            this.salonRepository.setUserSalon(trans.userSalon);
            this.salonRepository.setToggleChecked(!!trans.userSalon?.selectedSalonId);
          }
          if (this.notificationsCollection.hasErrors) {
            const enhanceNotificationsCollectionUseCase = new EnhanceNotificationsCollectionUseCase();
            enhanceNotificationsCollectionUseCase.execute();
          }
          const checkTransactionBreakingMissingData = new CheckTransactionBreakingMissingData();
          checkTransactionBreakingMissingData.execute();
        });
        return resolve(this.transaction);
      } catch (error) {
        if (error instanceof ApiError && error.status === 403) {
          this.hasIrreconcilableError = true;
          return this;
        } else {
          return reject(error);
        }
      } finally {
        // keep the original promise reference if we have a 403, otherwise clear the promise
        if (!this.hasIrreconcilableError) {
          this.transactionPromise = null;
        }
        this.transaction.setIsDirty(false);
      }
    });
    return this.transactionPromise;
  };

  // update user's available addresses and selected one based on transaction response
  private setShippingAddresses(transactionResponse: IFromTransactionMapping): void {
    const { shippingAddress, userShippingAddresses } = transactionResponse;

    this.shippingRepository.setAddresses(userShippingAddresses || []);

    if (shippingAddress?.[FIELD_TYPE.ID] && !this.shippingRepository.getAddressById(shippingAddress?.[FIELD_TYPE.ID])) {
      this.shippingRepository.addAddress(Address.fromJSON(shippingAddress));
    }

    // BOPIS Addresses set at the transaction level need not be set as the shipping address here
    // This is to avoid the shipping address validations for BOPIS types in other places. 
    if (shippingAddress && this.shouldSelectShippingAddress && shippingAddress[FIELD_TYPE.ADDRESS_TYPE] !== ADDRESS_TYPE_ID.BOPIS) {
      this.transaction.setShippingAddress(Address.fromJSON(shippingAddress));
    }

    // select appropiate shipping address in case the order address is not eligible or missing
    if (!this.transaction.shippingAddress && this.shippingRepository.addresses.length) {
      const defaultAddress = this.shippingRepository.addresses.find(item => item.isDefault);
      const [firstAddress] = this.shippingRepository.addresses;
      this.transaction.setShippingAddress(defaultAddress || firstAddress);
    }
  }

  @action
  setBillingAddress = (transaction: IFromTransactionMapping): void => {
    const {
      billingAddress,
      shippingAddress,
    } = transaction;

    const {
      billingRepository,
    } = this;

    // TODO: Remove check for guest after RCO-9408
    if (this.transaction.user.isUserRegistered && transaction?.userAddresses ) {
      billingRepository.setAddresses(transaction.userAddresses);
    }

    if (billingAddress?.[FIELD_TYPE.ID]) {
      // if transaction billing address not added to billing address list then add it
      if (!billingRepository.getAddressById(billingAddress?.[FIELD_TYPE.ID])) {
        billingRepository.addAddress(Address.fromJSON(billingAddress));
      }
      if (this.shouldSelectBillingAddress) {
        if (shippingAddress?.[FIELD_TYPE.ID] === billingAddress?.[FIELD_TYPE.ID]
          && !this.transaction.shippingAddress.isNewAddress
        ) {
          this.transaction.setBillingAddress(this.transaction.shippingAddress);
        } else {
          this.transaction.setBillingAddress(Address.fromJSON(transaction.billingAddress));
        }
      }
    } else if (
      shippingAddress?.[FIELD_TYPE.ID]
      && shippingAddress[FIELD_TYPE.ADDRESS_TYPE] === ADDRESS_TYPE_ID.SHIPPING
      && !transaction.billingAddress
      && this.transaction.billingAddress.isNewAddress
    ) {
      // set billing as shipping in case there is one and billing is missing from transaction and there is no previous billing available
      billingRepository.addAddress(Address.fromJSON(shippingAddress));
      this.transaction.setBillingAddress(this.transaction.shippingAddress);
    }
  };

  @action
  setShippingMethod = async (
    shippingMethodId: SHIPPING_METHODS,
    debounceRequest?: boolean,
    refreshTransaction = true,
    options?: { signal?: AbortSignal },
  ): Promise<IFromShippingMethodMapping | void> => {
    const {
      selectedShippingMethodId: previousSelectedMethod,
      namedDelivery,
      shippingMethods: { items: currentShipMethods },
      getShippingMethodById,
      setMethods,
      setSelectedShippingMethodId,
      updateOrderSummary,
    } = this.transaction;

    try {
      const newOrEditShippingCalculator = this.configRepository.config?.useShippingCalculator && this.isNewOrEditAddress;
      const zipCode = newOrEditShippingCalculator ? this.shippingRepository.formStore?.values?.[FIELD_TYPE.ZIPCODE] : '';
      const reloadTransaction = !(newOrEditShippingCalculator || shippingMethodId === SHIPPING_METHODS.LOCAL_COLLECTION) && refreshTransaction;
      const selectedShippingMethod = getShippingMethodById(shippingMethodId);
      if (previousSelectedMethod !== shippingMethodId && selectedShippingMethod?.availableWindow) {
        const { startHour, stopHour, timeZone } = selectedShippingMethod.availableWindow;
        // If startHour is 2400 (12:00AM), change it to 0.
        // Otherwise the shipmethod will always be removed
        const newStartHour = startHour === 2400 ? 0 : startHour;
        const currentMilitaryTime = DateUtils.getCurrentTimeInMilitary(DateUtils.getDateFromTimeZone(timeZone));
        if (currentMilitaryTime <= newStartHour || currentMilitaryTime >= stopHour) {
          // Remove invalid shipmethod and set them again
          setMethods(currentShipMethods.filter( m => m.id !== shippingMethodId), DeliveryOptions.HOME_DELIVERY);
          return;
        }  
      }
      setSelectedShippingMethodId(shippingMethodId);

      const selectedCountryId = this.isNewOrEditAddress ? this.shippingRepository.formStore.values[FIELD_TYPE.COUNTRY_ID] :
        this.transaction.shippingAddress?.attributes?.country_id;

      const response = debounceRequest
        ? await this.api.setShippingMethodDebounced(shippingMethodId, namedDelivery, zipCode as string, selectedCountryId, options)
        : await this.api.setShippingMethod(shippingMethodId, namedDelivery, zipCode as string, selectedCountryId, options);
      updateOrderSummary(response.orderSummary as Order);

      if (!this.shouldSaveAddressPriorShippingMethod) {
        if (reloadTransaction && previousSelectedMethod !== shippingMethodId) {
          await this.loadTransaction();
        }
      }

      return response;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setSelectedShippingMethodId(previousSelectedMethod);
      return e;
    }
  };

  @action
  loadShippingMethods = async (countryId: number, options: {
    debounceRequest: boolean,
    reloadTransaction: boolean,
    reloadAfterSetDefaultMethod?: boolean,
    signal?: AbortSignal,
  } = {
    debounceRequest: false,
    reloadAfterSetDefaultMethod: true,
    reloadTransaction: true,
  } ): Promise<void> => {
    try {
      const shippingMethods = await this.loadHomeDeliveryMethods(countryId, { signal: options?.signal });
      // auto select the first shipping method if no shipping method has selected yet
      if (shippingMethods && shippingMethods.length && !shippingMethods.find(method => method.id === this.transaction.selectedShippingMethodId)) {
        const defaultSelectedShippingMethod = shippingMethods[0];
        await this.setShippingMethod(
          defaultSelectedShippingMethod.id,
          options.debounceRequest,
          options.reloadTransaction,
          options.signal ? { signal: options.signal } : undefined,
        );

        if (options.reloadAfterSetDefaultMethod) {
          // for cross border shipping sites only, after new ship method has set, the BE may apply new offer and need to 
          // get the latest shipping methods again for cross border shipping sites
          this.loadShippingMethodsForCrossBorderSite(countryId);
        }
      }
    } catch (e) {
      if (options.signal?.aborted) {
        throw e;
      }
      // eslint-disable-next-line no-console
      console.error(e);
      return e;
    }
  };

  @action
  public loadShippingMethodsForCrossBorderSite = async (countryId?: number): Promise<void> => {
    if (this.configRepository.hasCrossBorderShipping && countryId) {
      this.loadHomeDeliveryMethods(countryId);
    }
  };

  private loadHomeDeliveryMethods = async (countryId: number, options?: { signal?: AbortSignal }): Promise<IShippingMethod[]> => {
    const shippingMethods = await this.api.getShippingMethods(countryId, undefined, options);
    this.transaction.setMethods(shippingMethods, DeliveryOptions.HOME_DELIVERY);

    return shippingMethods;
  };

  @action
  public updateEstimatedDeliveryDateResponse = (mapping: IFromEstimatedDeliveryMapping): void => {
    const shippingMethods: IShippingMethod[] = mapping?.deliveryOptions?.find(
      item => item.name === DeliveryOptions.HOME_DELIVERY,
    )?.shipMethods || [];
    this.transaction.setMethods(shippingMethods, DeliveryOptions.HOME_DELIVERY);
    this.transaction.setCart(mapping.cartItems);
  };

  @computed
  get transKey(): string { return this.transaction.transKey; }

  // For guest user or new address, we are enforced to save address prior saving shipping method
  // Changing an address country might require a different delivery method
  // Hence we will run into issues if the delivery method doesn't match the address country 
  @computed
  get shouldSaveAddressPriorShippingMethod(): boolean {
    const {
      user: {
        isGuestUser,
      },
      shippingAddress,
    } = this.transaction;

    return (isGuestUser || shippingAddress.isNewAddress) && this.configRepository.config.shippingCountries?.length > 1;
  }

  @computed
  public get isNewOrEditAddress(): boolean {
    const { shippingAddress } = this.transaction;
    // guest user may edit saved address which is also editing address but not belongs to
    // isEditing or isNewAddress status
    return this.shippingRepository.isEditing || shippingAddress.isNewAddress || this.transaction.isGuestOrPOA;
  }
}
